import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { Reveal, SectionHeading, Icon, Counter } from "@/components/common/motion";
import { Testimonials, ContactCTA } from "@/components/shared/blocks";
import { company, values, timeline, leadership, stats } from "@/data/site";

export default function About() {
  return (
    <>
      <SEO title="About Us" description="The story, mission and people behind NeuroTriq TechLabs — an IT services and technology consulting firm founded in 2015." path="/about" />
      <PageHeader
        overline="About Us"
        title="We build reliable digital solutions for growing businesses"
        subtitle={`Founded in ${company.founded}, NeuroTriq TechLabs partners with enterprises, SMBs and startups to design, build and scale technology that moves the business forward.`}
      />

      {/* Story + image */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="mb-4 flex items-center gap-2">
              <span className="h-px w-8 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-primary">Our story</span>
            </div>
            <h2 className="font-display text-3xl font-medium tracking-tight md:text-4xl">Why we started</h2>
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
              <p>NeuroTriq TechLabs began with a simple belief: technology should be a dependable engine for growth, not a source of risk and frustration. Too many businesses were sold complexity they didn&apos;t need and left to maintain systems that couldn&apos;t scale.</p>
              <p>We set out to be different — a partner that pairs senior engineering talent with radical transparency and a genuine focus on business outcomes. A decade later, that principle still guides every engagement.</p>
              <p>Today our teams span software, cloud, data, AI and security, delivering across a global model that combines onshore accountability with distributed delivery speed.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl border border-border shadow-xl">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80" alt="NeuroTriq team collaborating" loading="lazy" className="h-[26rem] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-muted py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-2">
          {[
            { icon: "Target", label: "Mission", text: "To empower businesses through smart, reliable technology — turning ambitious ideas into secure, scalable products that deliver measurable value." },
            { icon: "Telescope", label: "Vision", text: "To be the most trusted technology partner for growing businesses worldwide, known for craft, transparency and outcomes that last." },
          ].map((m, i) => (
            <Reveal key={m.label} delay={i * 0.1}>
              <div className="h-full rounded-3xl border border-border bg-card p-8 sm:p-10">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary"><Icon name={m.icon} className="h-6 w-6" /></span>
                <h3 className="mt-6 font-display text-2xl font-medium">{m.label}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionHeading overline="Our Values" title="What we stand for" subtitle="The principles that shape how we work, decide and treat the people we serve." /></Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-card p-6">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent/10 text-accent"><Icon name={v.icon} className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-display text-lg font-medium">{v.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-y border-border bg-secondary py-16 text-secondary-foreground">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 sm:px-8 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center lg:text-left">
              <p className="font-display text-5xl font-semibold text-white"><Counter value={s.value} suffix={s.suffix} /></p>
              <p className="mt-2 text-sm text-white/60">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal><SectionHeading overline="Journey" title="A decade of building" subtitle="Key milestones on the road from a three-person studio to a trusted delivery partner." /></Reveal>
          <div className="mt-14 space-y-2">
            {timeline.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.05}>
                <div className="group relative grid grid-cols-[auto_1fr] gap-6 rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40">
                  <span className="font-display text-2xl font-semibold text-primary">{t.year}</span>
                  <div>
                    <h3 className="font-display text-lg font-medium">{t.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-muted py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionHeading overline="Leadership" title="The people behind the work" subtitle="Senior operators and builders who lead by example." /></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {leadership.map((l, i) => (
              <Reveal key={l.name} delay={(i % 4) * 0.08}>
                <div className="group overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="relative h-64 overflow-hidden">
                    <img src={l.avatar} alt={l.name} loading="lazy" className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-medium">{l.name}</h3>
                    <p className="text-sm text-primary">{l.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Global delivery + culture */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-border bg-card p-8 sm:p-10">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary"><Icon name="Globe2" className="h-6 w-6" /></span>
              <h3 className="mt-6 font-display text-2xl font-medium">Global delivery model</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">Onshore accountability meets distributed delivery. A named lead owns your outcomes while our global teams give you speed, coverage and 24×7 support.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-border bg-card p-8 sm:p-10">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent/10 text-accent"><Icon name="Sparkles" className="h-6 w-6" /></span>
              <h3 className="mt-6 font-display text-2xl font-medium">Our culture</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">We hire kind, curious, senior people and give them ownership. We sweat the details, share knowledge openly and celebrate outcomes over ego.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <Testimonials />
      <ContactCTA />
    </>
  );
}
